import { LegalLayout, ContactCard, type LegalSection } from './LegalLayout'
import { APP_NAME } from '@/utils/constants'

const SECTIONS: LegalSection[] = [
  { id: 'acceptance', title: '1. Acceptance of Terms' },
  { id: 'definitions', title: '2. Definitions' },
  { id: 'accounts', title: '3. Account Registration & Management' },
  { id: 'subscriptions', title: '4. Subscriptions & Billing' },
  { id: 'platform', title: '5. Platform Access & Usage' },
  { id: 'guests', title: '6. Guests, Share Keys & Public Content' },
  { id: 'mobile', title: '7. Mobile Applications' },
  { id: 'content', title: '8. User Content & Conduct' },
  { id: 'intellectual', title: '9. Intellectual Property' },
  { id: 'privacy', title: '10. Privacy & Data Protection' },
  { id: 'prohibited', title: '11. Prohibited Activities' },
  { id: 'termination', title: '12. Termination & Suspension' },
  { id: 'warranties', title: '13. Warranties & Disclaimers' },
  { id: 'liability', title: '14. Limitation of Liability' },
  { id: 'indemnification', title: '15. Indemnification' },
  { id: 'disputes', title: '16. Dispute Resolution' },
  { id: 'governing', title: '17. Governing Law' },
  { id: 'changes', title: '18. Changes to Terms' },
  { id: 'general', title: '19. General Provisions' },
  { id: 'contact', title: '20. Contact Information' },
]

const updatedLabel = `Last updated: ${new Date().toLocaleDateString('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
})}`

export function TermsPage() {
  return (
    <LegalLayout eyebrow='Legal' title='Terms of Service' updatedLabel={updatedLabel} sections={SECTIONS}>
      <section id='acceptance'>
        <h2>1. Acceptance of Terms</h2>
        <p>
          By accessing or using any {APP_NAME} services (&ldquo;Services&rdquo;), including our web
          platform, mobile applications (iOS and Android), API, or any related services, you
          acknowledge that you have read, understood, and agree to be bound by these Terms of
          Service (&ldquo;Terms&rdquo;) and our Privacy Policy.
        </p>
        <p>
          If you are accessing or using the Services on behalf of a company, organization, or
          other legal entity, you represent and warrant that you have the authority to bind such
          entity to these Terms, and your acceptance of these Terms shall be deemed an acceptance
          by such entity.
        </p>
        <p>
          If you do not agree to these Terms, you must not access or use the Services. Your
          continued use of the Services constitutes your acceptance of these Terms as they may be
          modified from time to time.
        </p>
      </section>

      <section id='definitions'>
        <h2>2. Definitions</h2>
        <p>
          <strong>&ldquo;Account&rdquo;</strong> means the user account created to access the
          Services.
        </p>
        <p>
          <strong>&ldquo;Content&rdquo;</strong> means any data, text, graphics, photos, videos, or
          other materials uploaded, posted, or transmitted through the Services.
        </p>
        <p>
          <strong>&ldquo;Album&rdquo; and &ldquo;Gallery&rdquo;</strong> mean a collection of
          Content organized by a User, which may be a flat album or a 3D walkable gallery.
        </p>
        <p>
          <strong>&ldquo;Guest&rdquo;</strong> means anyone who views, reacts to, or comments on an
          Album or Gallery via a share link or key without creating an Account.
        </p>
        <p>
          <strong>&ldquo;Subscription&rdquo;</strong> means a paid plan that provides access to
          premium features of the Services.
        </p>
        <p>
          <strong>&ldquo;User,&rdquo; &ldquo;You,&rdquo; or &ldquo;Your&rdquo;</strong> means the
          individual or entity accessing or using the Services.
        </p>
        <p>
          <strong>&ldquo;{APP_NAME},&rdquo; &ldquo;We,&rdquo; &ldquo;Us,&rdquo; or
          &ldquo;Our&rdquo;</strong> means Egowall, LLC and its affiliates, subsidiaries, and
          related entities.
        </p>
      </section>

      <section id='accounts'>
        <h2>3. Account Registration &amp; Management</h2>
        <h3>3.1 Account Creation</h3>
        <p>
          To access certain features of the Services, you must create an Account. You must provide
          accurate, complete, and current information during registration and maintain the accuracy
          of this information throughout your use of the Services.
        </p>
        <h3>3.2 Age Requirements</h3>
        <p>
          You must be at least 18 years old to create an Account. If you are between 13 and 18
          years old, you may only use the Services with parental or guardian consent. We do not
          knowingly collect information from children under 13.
        </p>
        <h3>3.3 Account Security</h3>
        <p>
          You are responsible for maintaining the confidentiality of your Account credentials and
          for all activities that occur under your Account. You must immediately notify us of any
          unauthorized access or security breach. We are not liable for any loss or damage arising
          from unauthorized Account access due to your failure to maintain security.
        </p>
        <h3>3.4 Account Types</h3>
        <p>
          We offer different Account types, including free (&ldquo;Keepsake&rdquo;) and paid
          (&ldquo;Gallery&rdquo; and &ldquo;Studio&rdquo;) tiers. Each Account type has specific
          features, limitations, and pricing as detailed on our{' '}
          <a href='/pricing'>Pricing</a> page.
        </p>
      </section>

      <section id='subscriptions'>
        <h2>4. Subscriptions &amp; Billing</h2>
        <h3>4.1 Subscription Plans</h3>
        <p>
          We offer various Subscription plans with different features and pricing. Subscription
          details, including pricing, features, and limitations, are available on our website and
          may change at our discretion with advance notice.
        </p>
        <h3>4.2 Payment &amp; Billing</h3>
        <p>
          Subscriptions are billed in advance on a recurring basis (monthly or annually, as
          selected). You authorize us to charge your designated payment method. If payment fails,
          we may suspend or terminate your access to paid features until payment is received.
        </p>
        <h3>4.3 Automatic Renewal</h3>
        <p>
          Subscriptions automatically renew at the end of each billing period unless canceled
          before the renewal date. You will be charged the then-current rate for your plan upon
          renewal.
        </p>
        <h3>4.4 Cancellation &amp; Refunds</h3>
        <p>
          You may cancel your Subscription at any time through your Account settings. Cancellation
          takes effect at the end of the current billing period. Content you created stays viewable
          at full resolution for you and anyone with a share key, even after cancellation — you
          lose the ability to create new Albums beyond the free tier, not the memories you already
          made. We do not provide refunds for partial billing periods except as required by law or
          at our sole discretion.
        </p>
        <h3>4.5 Price Changes</h3>
        <p>
          We may change Subscription prices at any time. Price changes will be communicated at
          least 30 days in advance and will apply to subsequent billing periods. Continued use
          after a price change constitutes acceptance of the new pricing.
        </p>
        <h3>4.6 Taxes</h3>
        <p>
          All fees are exclusive of applicable taxes, duties, or similar governmental assessments.
          You are responsible for all taxes except those based on our net income.
        </p>
      </section>

      <section id='platform'>
        <h2>5. Platform Access &amp; Usage</h2>
        <h3>5.1 License Grant</h3>
        <p>
          Subject to your compliance with these Terms, we grant you a limited, non-exclusive,
          non-transferable, revocable license to access and use the Services for your personal or
          internal business purposes.
        </p>
        <h3>5.2 Service Availability</h3>
        <p>
          We strive to maintain high uptime but do not guarantee uninterrupted or error-free
          access. We may temporarily suspend access for maintenance, updates, or to address
          security issues.
        </p>
        <h3>5.3 Usage Limitations</h3>
        <p>
          Your use of the Services is subject to limitations based on your Subscription plan,
          including but not limited to: number of Albums and Galleries, number of share keys, and
          feature access. Exceeding these limits may result in additional charges or service
          restrictions.
        </p>
        <h3>5.4 System Requirements</h3>
        <p>
          You are responsible for obtaining and maintaining all equipment and services needed for
          access to the Services, including computers, mobile devices, internet access, and
          compatible software.
        </p>
      </section>

      <section id='guests'>
        <h2>6. Guests, Share Keys &amp; Public Content</h2>
        <h3>6.1 Sharing Your Content</h3>
        <p>
          When you set an Album or Gallery to &ldquo;anyone with the link&rdquo; or generate a
          share key or QR code, you are choosing to make that Content viewable by anyone who has
          the link or key, without requiring them to create an Account. You can revoke a share key
          at any time; revoking a key immediately ends Guest access through that key.
        </p>
        <h3>6.2 Guest Access</h3>
        <p>
          Guests may view Content, and — where the host allows it — react to and comment on
          Content, without creating an Account. Guests are bound by the &ldquo;User Content &amp;
          Conduct&rdquo; and &ldquo;Prohibited Activities&rdquo; sections of these Terms for any
          reaction, comment, or other input they submit, even without an Account.
        </p>
        <h3>6.3 Public Discovery</h3>
        <p>
          Albums and Galleries you mark as public may appear in Discover surfaces shown to other
          Users and visitors. You can change an Album or Gallery back to private at any time,
          which removes it from Discover and invalidates unrevoked share keys' public visibility
          going forward.
        </p>
        <h3>6.4 No Account Required to Browse</h3>
        <p>
          Certain Services — including public Discover pages, the guest pattern editor, and public
          Album/Gallery links — are available without an Account. These sections of the Terms still
          apply to that use, except that obligations specific to Accounts (Section 3) and billing
          (Section 4) do not apply until an Account is created.
        </p>
      </section>

      <section id='mobile'>
        <h2>7. Mobile Applications</h2>
        <h3>7.1 Mobile App License</h3>
        <p>
          Subject to these Terms, we grant you a limited, non-exclusive, non-transferable license
          to download, install, and use our mobile applications on devices you own or control.
        </p>
        <h3>7.2 App Store Terms</h3>
        <p>
          When downloading our mobile applications from app stores (Apple App Store, Google Play
          Store), you agree to comply with their respective terms of service. In case of conflict
          between these Terms and app store terms, the app store terms govern with respect to the
          download and installation.
        </p>
        <h3>7.3 Push Notifications</h3>
        <p>
          Our mobile applications may send push notifications. You can disable push notifications
          through your device settings at any time.
        </p>
      </section>

      <section id='content'>
        <h2>8. User Content &amp; Conduct</h2>
        <h3>8.1 User Content</h3>
        <p>
          You may submit, upload, or otherwise make available Content through the Services. You
          retain all ownership rights to your Content. By submitting Content, you grant us a
          worldwide, non-exclusive, royalty-free license to use, store, display, and distribute
          your Content solely to provide and improve the Services.
        </p>
        <h3>8.2 Content Responsibility</h3>
        <p>
          You are solely responsible for your Content. You represent and warrant that you own or
          have necessary rights to all Content you submit and that your Content does not violate
          any laws, regulations, or third-party rights.
        </p>
        <h3>8.3 Content Standards</h3>
        <p>
          Your Content must not: (a) be illegal, harmful, threatening, abusive, harassing,
          defamatory, or invasive of privacy; (b) infringe intellectual property rights; (c)
          contain viruses or malicious code; (d) be spam or unsolicited communications; or (e)
          violate any applicable laws or regulations.
        </p>
        <h3>8.4 Content Monitoring</h3>
        <p>
          We reserve the right, but have no obligation, to monitor, edit, or remove Content that
          violates these Terms or is otherwise objectionable. We do not pre-screen Content and are
          not responsible for Content submitted by Users or Guests.
        </p>
        <h3>8.5 Content Backup</h3>
        <p>
          While we maintain regular backups, you are responsible for maintaining your own backups
          of your Content. We are not liable for any loss or corruption of Content.
        </p>
      </section>

      <section id='intellectual'>
        <h2>9. Intellectual Property</h2>
        <h3>9.1 Our Intellectual Property</h3>
        <p>
          The Services and all associated intellectual property rights are owned by {APP_NAME} or
          our licensors. This includes but is not limited to: software, code, databases, logos,
          trademarks, user interface, design elements, and documentation. Nothing in these Terms
          grants you any right to use our trademarks, logos, or other brand elements.
        </p>
        <h3>9.2 Restrictions</h3>
        <p>
          You may not: (a) copy, modify, or create derivative works of the Services; (b) reverse
          engineer, decompile, or disassemble the Services; (c) rent, lease, sell, or sublicense
          the Services; (d) remove or modify any proprietary notices; or (e) use the Services to
          develop competing products or services.
        </p>
        <h3>9.3 Feedback</h3>
        <p>
          If you provide feedback, suggestions, or ideas about the Services, you grant us an
          unrestricted, perpetual, irrevocable license to use such feedback without compensation or
          attribution.
        </p>
        <h3>9.4 Copyright Infringement</h3>
        <p>
          We respect intellectual property rights. If you believe Content on our Services
          infringes your copyright, please contact us at{' '}
          <a href='mailto:thana@egowall.com'>thana@egowall.com</a> with: (a) identification of the
          copyrighted work; (b) location of the infringing material; (c) your contact information;
          (d) a statement of good faith belief; and (e) a statement under penalty of perjury that
          the information is accurate and you are authorized to act on behalf of the copyright
          owner.
        </p>
      </section>

      <section id='privacy'>
        <h2>10. Privacy &amp; Data Protection</h2>
        <p>
          Your privacy is important to us. Our <a href='/privacy'>Privacy Policy</a> explains how
          we collect, use, and protect your personal information. By using the Services, you
          consent to our data practices as described in the Privacy Policy.
        </p>
        <p>
          We comply with applicable data protection laws including GDPR, CCPA, and other
          international privacy frameworks. You have rights regarding your personal data, including
          access, correction, deletion, and portability rights as described in our Privacy Policy.
        </p>
      </section>

      <section id='prohibited'>
        <h2>11. Prohibited Activities</h2>
        <p>You agree not to engage in any of the following prohibited activities:</p>
        <ul>
          <li>Violating any laws, regulations, or third-party rights</li>
          <li>Impersonating any person or entity</li>
          <li>Attempting to gain unauthorized access to the Services or systems</li>
          <li>Introducing viruses, malware, or harmful code</li>
          <li>Interfering with or disrupting the Services or servers</li>
          <li>Harvesting or collecting User or Guest information</li>
          <li>Using automated systems to access the Services</li>
          <li>Reselling or distributing the Services</li>
          <li>Using the Services for illegal activities or to facilitate illegal activities</li>
          <li>Circumventing any security features or access controls, including share-key restrictions</li>
          <li>Overloading or attempting to overwhelm our infrastructure</li>
          <li>Creating multiple accounts to evade restrictions or limitations</li>
        </ul>
      </section>

      <section id='termination'>
        <h2>12. Termination &amp; Suspension</h2>
        <h3>12.1 Termination by You</h3>
        <p>
          You may terminate your Account at any time through Account settings or by contacting
          support. Termination does not relieve you of obligations to pay any outstanding fees.
        </p>
        <h3>12.2 Termination by Us</h3>
        <p>
          We may suspend or terminate your access to the Services immediately, without prior
          notice, for: (a) violation of these Terms; (b) non-payment of fees; (c) suspected
          fraudulent or illegal activity; (d) violation of laws or regulations; or (e) upon your
          request.
        </p>
        <h3>12.3 Effect of Termination</h3>
        <p>
          Upon termination: (a) your right to use the Services immediately ceases; (b) we may
          delete your Content and Account data after 30 days; (c) provisions that by their nature
          should survive termination will survive, including intellectual property provisions,
          disclaimers, and limitations of liability.
        </p>
      </section>

      <section id='warranties'>
        <h2>13. Warranties &amp; Disclaimers</h2>
        <p>
          THE SERVICES ARE PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo; WITHOUT
          WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED
          WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
        </p>
        <p>
          WE DO NOT WARRANT THAT: (A) THE SERVICES WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE;
          (B) DEFECTS WILL BE CORRECTED; (C) THE SERVICES ARE FREE FROM VIRUSES OR HARMFUL
          COMPONENTS; OR (D) RESULTS FROM USING THE SERVICES WILL MEET YOUR REQUIREMENTS.
        </p>
        <p>
          Some jurisdictions do not allow exclusion of implied warranties, so some exclusions may
          not apply to you.
        </p>
      </section>

      <section id='liability'>
        <h2>14. Limitation of Liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL {APP_NAME.toUpperCase()}, ITS
          DIRECTORS, EMPLOYEES, PARTNERS, AGENTS, SUPPLIERS, OR AFFILIATES BE LIABLE FOR ANY
          INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT
          LIMITED TO LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
        </p>
        <p>
          OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM OR RELATED TO THE SERVICES SHALL NOT
          EXCEED THE GREATER OF: (A) THE AMOUNT YOU PAID US IN THE 12 MONTHS PRECEDING THE CLAIM;
          OR (B) $100.
        </p>
        <p>
          Some jurisdictions do not allow limitation of liability for certain damages, so some
          limitations may not apply to you.
        </p>
      </section>

      <section id='indemnification'>
        <h2>15. Indemnification</h2>
        <p>
          You agree to indemnify, defend, and hold harmless {APP_NAME} and its directors, officers,
          employees, agents, and affiliates from any claims, demands, losses, liabilities, and
          expenses (including attorneys' fees) arising from: (a) your use of the Services; (b) your
          violation of these Terms; (c) your violation of any rights of another party; or (d) your
          Content.
        </p>
      </section>

      <section id='disputes'>
        <h2>16. Dispute Resolution</h2>
        <h3>16.1 Informal Resolution</h3>
        <p>
          In the event of any controversy or claim arising out of or relating to these Terms, the
          parties agree to first attempt to resolve the dispute informally by contacting{' '}
          <a href='mailto:thana@egowall.com'>thana@egowall.com</a>.
        </p>
        <h3>16.2 Binding Arbitration</h3>
        <p>
          If informal resolution fails, any dispute shall be resolved by binding arbitration in
          accordance with the rules of the American Arbitration Association. The arbitration shall
          be conducted in English in the jurisdiction specified in Section 17.
        </p>
        <h3>16.3 Class Action Waiver</h3>
        <p>
          You agree that any arbitration or proceeding shall be limited to the dispute between you
          and us individually. To the full extent permitted by law, no arbitration or proceeding
          shall be joined with any other or brought on a class-action basis.
        </p>
      </section>

      <section id='governing'>
        <h2>17. Governing Law</h2>
        <p>
          These Terms shall be governed by and construed in accordance with the laws of the State
          of Texas, USA, without regard to its conflict of law principles. You agree to submit to
          the exclusive jurisdiction of the courts located in Dallas County, Texas for resolution
          of any disputes not subject to arbitration.
        </p>
      </section>

      <section id='changes'>
        <h2>18. Changes to Terms</h2>
        <p>
          We reserve the right to modify these Terms at any time. We will provide notice of
          material changes by email, through the Services, or by other means at least 30 days
          before the effective date of the changes.
        </p>
        <p>
          Your continued use of the Services after the effective date of revised Terms constitutes
          your acceptance of the changes. If you do not agree to the modified Terms, you must stop
          using the Services.
        </p>
      </section>

      <section id='general'>
        <h2>19. General Provisions</h2>
        <h3>19.1 Entire Agreement</h3>
        <p>
          These Terms, together with the Privacy Policy and any other legal notices published on
          the Services, constitute the entire agreement between you and {APP_NAME}.
        </p>
        <h3>19.2 Severability</h3>
        <p>
          If any provision of these Terms is found to be unenforceable or invalid, that provision
          shall be limited or eliminated to the minimum extent necessary so that these Terms shall
          otherwise remain in full force and effect.
        </p>
        <h3>19.3 Assignment</h3>
        <p>
          You may not assign or transfer these Terms or your rights hereunder without our prior
          written consent. We may assign these Terms without restriction.
        </p>
        <h3>19.4 Waiver</h3>
        <p>
          Our failure to enforce any right or provision of these Terms will not be deemed a waiver
          of such right or provision.
        </p>
      </section>

      <section id='contact'>
        <h2>20. Contact Information</h2>
        <p>If you have any questions about these Terms of Service, please contact us:</p>
        <ContactCard>
          <p>
            <strong>Email:</strong> <a href='mailto:thana@egowall.com'>thana@egowall.com</a>
          </p>
          <p>
            <strong>Mailing Address:</strong>
            <br />
            Egowall, LLC
            <br />
            Attn: Terms and Conditions Issues
            <br />
            3102 Oak Lawn Avenue, Suite 505
            <br />
            Dallas, TX 75219, USA
          </p>
        </ContactCard>
      </section>
    </LegalLayout>
  )
}
